import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, AlertTitle } from '@mui/material';
import { MdOutlineWarning } from 'react-icons/md';
import walkthroughIds from '../walkthroughIds';
import { axios } from '@/services/axios';
import { Box, Text, Button as ChakraButton } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { toast } from 'react-toastify';

const buttonStyle = { justifyContent: 'center', paddingBottom: '12px' };

// A admin tab for trunks with a button that deletes the trunk with a 'are you sure?' confirmation
const EditTrunkAdminTab = ({ trunkValid, trunkName }) => {
    const { id } = useParams();
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isDisabled, setIsDisabled] = useState(false);

    // state access
    const { darkMode } = useSelector((state) => state.settings);

    // logic for open and close
    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setError(false);
    };

    // axios calls for delete and dispatches
    const deleteHandler = () => {
        setIsDisabled(true);
        axios
            .delete('/trunk/' + id)
            .then(() => {
                navigate('/trunks');
                dispatch({
                    type: 'RESET_MODAL',
                });
                dispatch({
                    type: 'RESET_TABLE',
                });
                toast.success(`${trunkName} trunk deleted successfully!`);
                return () => handleClose();
            })
            .catch((error) => {
                setErrorMessage(error.response.data.message);
                setError(true);
                setIsDisabled(false);
            });
    };

    return (
        <>
            {/* Incomplete trunk alert shows if the trunkValid prop is false */}
            {!trunkValid && (
                <Alert
                    severity="warning"
                    variant="filled"
                    style={{ marginTop: 5, marginBottom: 15 }}>
                    <AlertTitle>Incomplete Trunk</AlertTitle>
                    You must add Collection Ports and Termination IPs before
                    trunk is operational.
                </Alert>
            )}
            <Box display="flex" alignItems="center" gap={4} mt={3} mb={3}>
                <Box>
                    <Text fontWeight="bold">Delete</Text>
                    <Text>
                        By deleting the trunk, it will remove all trunk
                        configuration and calls will stop processing
                    </Text>
                </Box>
                <ChakraButton
                    colorScheme="red"
                    borderRadius="5px"
                    flexShrink={0}
                    onClick={handleOpen}
                    data-walkthroughid={walkthroughIds.ufDeleteButton}>
                    Delete
                </ChakraButton>
            </Box>
            <Dialog
                open={open}
                onClose={handleClose}
                sx={{
                    '& .MuiPaper-root': {
                        bgcolor: darkMode ? '#212121' : null,
                        color: darkMode ? 'white' : 'inherit',
                    },
                }}>
                {!error && (
                    <>
                        <DialogTitle>Delete Trunk</DialogTitle>
                        <DialogContent>
                            <DialogContentText
                                sx={{
                                    color: darkMode ? 'white' : 'inherit',
                                }}>
                                Are you sure you want to delete this trunk? This
                                cannot be undone
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={buttonStyle}>
                            <Button
                                color="error"
                                variant="contained"
                                onClick={() => {
                                    deleteHandler();
                                }}
                                disabled={isDisabled}
                                data-walkthroughid={
                                    walkthroughIds.ufDeleteModal
                                }>
                                Delete
                            </Button>
                        </DialogActions>
                    </>
                )}
                {error && (
                    <>
                        <DialogTitle>Delete Trunk</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                <div className="border-l-4 border-red-400 bg-red-50 p-4">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <MdOutlineWarning
                                                className="h-5 w-5 text-red-400"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-base text-red-700">
                                                {errorMessage}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={buttonStyle}>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={() => handleClose()}
                                data-walkthroughid={
                                    walkthroughIds.ufCloseModal
                                }>
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default EditTrunkAdminTab;
